import { useCallback, useState } from "react";
import type { ReactNode } from "react";

import { CheckCircle2, Pencil, RotateCcw, UserPlus } from "lucide-react";

import {
  normalizeFrenchPhoneCandidate,
  SEARCH_EMAIL_REGEX,
  SEARCH_FRENCH_PHONE_REGEX,
  SEARCH_NAME_REGEX,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/client-search-validation";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type { ParticulierSearchOutcome } from "@/lib/cat/client-step-contracts";
import type { RetailIndividual } from "@/lib/cat/retail-client.types";
import { cn } from "@/lib/utils";

type EditableIndividualProfile = {
  addressLine: string;
  city: string;
  email: string;
  phone: string;
  postalCode: string;
};

type CreationConfig = {
  emptyResultsLabel: string;
  openCreateLabel: string;
  closeCreateLabel: string;
  onCreateCanProceedChange: (v: boolean) => void;
  renderCreateForm: (onCanProceedChange: (v: boolean) => void) => ReactNode;
};

type NoResultsAlertConfig = {
  description: string;
  title: string;
};

type NoInternalCollaboratorAlertConfig = {
  description?: string;
  title: string;
};

function isTrimmedFieldModified(current: string, original: string): boolean {
  return current.trim() !== original.trim();
}

/** Original MDM text to show under the field; omit line when blank (friendlier UX). */
function previousValueLabel(original: string): string | null {
  const t = original.trim();
  return t.length > 0 ? t : null;
}

function editedFieldPreviousLine(
  fiche: EditableIndividualProfile,
  mdm: RetailIndividual,
  field: "addressLine" | "postalCode" | "city" | "phone" | "email",
): string | null {
  if (!isTrimmedFieldModified(fiche[field], mdm[field])) return null;
  return previousValueLabel(mdm[field]);
}

function IndividualsSection({
  additionalDetails,
  creationConfig,
  criteriaLabel = "Critères de recherche",
  editAriaLabelPrefix = "Modifier la fiche de",
  editBadgeLabel = "Modifications en cours",
  emailPlaceholder,
  ficheEdit,
  idPrefix,
  isSearchLoading = false,
  newSearchButtonLabel = "Nouvelle recherche",
  noInternalCollaboratorAlert,
  noResultsAlert,
  onFicheChange,
  onResetSearch,
  onSearch,
  onSelectIndividual,
  outcome,
  pEmail,
  pFirstName,
  pLastName,
  pPhone,
  resultsLabel,
  searchButtonLabel,
  sectionTitle,
  selectedBadgeLabel = "Fiche sélectionnée",
  selectedIndividual,
  selectedIndividualId,
  setPEmail,
  setPFirstName,
  setPLastName,
  setPPhone,
}: {
  additionalDetails?: (individual: RetailIndividual) => ReactNode;
  creationConfig?: CreationConfig;
  criteriaLabel?: string;
  editAriaLabelPrefix?: string;
  editBadgeLabel?: string;
  emailPlaceholder: string;
  ficheEdit: EditableIndividualProfile | null;
  idPrefix: string;
  isSearchLoading?: boolean;
  newSearchButtonLabel?: string;
  noInternalCollaboratorAlert?: NoInternalCollaboratorAlertConfig;
  noResultsAlert?: NoResultsAlertConfig;
  onFicheChange: (v: EditableIndividualProfile | null) => void;
  onResetSearch?: () => void;
  onSearch: () => void;
  onSelectIndividual: (id: string | null) => void;
  outcome: ParticulierSearchOutcome | null;
  pEmail: string;
  pFirstName: string;
  pLastName: string;
  pPhone: string;
  resultsLabel: (count: number) => string;
  searchButtonLabel: string;
  sectionTitle: string;
  selectedBadgeLabel?: string;
  selectedIndividual: RetailIndividual | null;
  selectedIndividualId: string | null;
  setPEmail: (v: string) => void;
  setPFirstName: (v: string) => void;
  setPLastName: (v: string) => void;
  setPPhone: (v: string) => void;
}) {
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const list = outcome?.kind === "list" ? outcome.individuals : [];
  const showServerError = outcome?.kind === "error";
  const showNoInternalCollaborator =
    outcome?.kind === "no_internal_collaborator";
  const showCreate =
    outcome?.kind === "list" && outcome.individuals.length === 0;
  const showList = outcome?.kind === "list" && outcome.individuals.length > 0;
  const showNoCriteria = outcome?.kind === "no_criteria";
  const normalizedLastName = pLastName.trim();
  const normalizedFirstName = pFirstName.trim();
  const hasLastName = normalizedLastName.length > 0;
  const hasFirstName = normalizedFirstName.length > 0;
  const isLastNameValid =
    hasLastName && SEARCH_NAME_REGEX.test(normalizedLastName);
  const isFirstNameValid =
    !hasFirstName || SEARCH_NAME_REGEX.test(normalizedFirstName);
  const hasEmail = pEmail.trim().length > 0;
  const hasPhone = pPhone.trim().length > 0;
  const normalizedPhone = normalizeFrenchPhoneCandidate(pPhone);
  const isEmailValid = !hasEmail || SEARCH_EMAIL_REGEX.test(pEmail.trim());
  const isPhoneValid =
    !hasPhone || SEARCH_FRENCH_PHONE_REGEX.test(normalizedPhone);
  const hasValidEmailOrPhone =
    (hasEmail && isEmailValid) || (hasPhone && isPhoneValid);
  const canSearch =
    isLastNameValid &&
    isFirstNameValid &&
    hasValidEmailOrPhone &&
    isEmailValid &&
    isPhoneValid;

  const handleSelectClient = useCallback(
    (id: string) => {
      setIsEditEnabled(false);
      onSelectIndividual(id);
    },
    [onSelectIndividual],
  );

  const handleEditClient = useCallback(
    (id: string) => {
      onSelectIndividual(id);
      if (selectedIndividualId === id && isEditEnabled) {
        setIsEditEnabled(false);
        return;
      }
      setIsEditEnabled(true);
    },
    [isEditEnabled, onSelectIndividual, selectedIndividualId],
  );

  const handleSearchClick = useCallback(() => {
    setIsEditEnabled(false);
    setShowCreateForm(false);
    creationConfig?.onCreateCanProceedChange(false);
    onSearch();
  }, [creationConfig, onSearch]);

  const handleToggleCreateForm = useCallback(() => {
    if (!creationConfig) return;
    setShowCreateForm((prev) => {
      const next = !prev;
      if (!next) {
        creationConfig.onCreateCanProceedChange(false);
      }
      return next;
    });
  }, [creationConfig]);

  const canShowCreateForm = showCreate && showCreateForm;

  const collapseSearchOnSuccess = showList && Boolean(onResetSearch);

  const handleNewSearch = useCallback(() => {
    setIsEditEnabled(false);
    setShowCreateForm(false);
    creationConfig?.onCreateCanProceedChange(false);
    onResetSearch?.();
  }, [creationConfig, onResetSearch]);

  return (
    <Card>
      <CardHeader
        className={cn(
          collapseSearchOnSuccess &&
            "flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-2",
        )}
      >
        <CardTitle className="text-base">{sectionTitle}</CardTitle>
        {collapseSearchOnSuccess ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNewSearch}
          >
            <RotateCcw className="mr-2 size-4" aria-hidden />
            {newSearchButtonLabel}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-6">
        {!collapseSearchOnSuccess ? (
          <>
            <div className="space-y-2">
              <p className="text-sm font-medium">{criteriaLabel}</p>
              <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                <li>Nom obligatoire</li>
                <li>
                  Email ou téléphone obligatoire (au moins l&apos;un des deux)
                </li>
                <li>Prénom optionnel</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field data-invalid={!isLastNameValid && hasLastName}>
                <FieldLabel htmlFor={`${idPrefix}-ln`}>
                  Nom <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-ln`}
                    value={pLastName}
                    onChange={(e) => setPLastName(e.target.value)}
                    placeholder="Ex: Blanc"
                    aria-invalid={!isLastNameValid && hasLastName}
                  />
                  {!isLastNameValid && hasLastName ? (
                    <FieldError>
                      Le nom doit contenir au moins 2 lettres.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field data-invalid={!isFirstNameValid && hasFirstName}>
                <FieldLabel htmlFor={`${idPrefix}-fn`}>
                  Prénom (optionnel)
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-fn`}
                    value={pFirstName}
                    onChange={(e) => setPFirstName(e.target.value)}
                    placeholder="Ex: Tom"
                    aria-invalid={!isFirstNameValid && hasFirstName}
                  />
                  {!isFirstNameValid && hasFirstName ? (
                    <FieldError>
                      Le prénom doit contenir au moins 2 lettres.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field data-invalid={!isEmailValid}>
                <FieldLabel htmlFor={`${idPrefix}-email`}>
                  Email <span className="text-destructive">*</span>
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    ou téléphone
                  </span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-email`}
                    type="email"
                    value={pEmail}
                    onChange={(e) => setPEmail(e.target.value)}
                    placeholder={emailPlaceholder}
                    aria-invalid={!isEmailValid}
                  />
                  {!isEmailValid ? (
                    <FieldError>
                      Veuillez saisir une adresse e-mail valide.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field>
                <FieldLabel htmlFor={`${idPrefix}-phone`}>
                  Téléphone <span className="text-destructive">*</span>
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    ou email
                  </span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-phone`}
                    value={pPhone}
                    onChange={(e) => setPPhone(e.target.value)}
                    placeholder="Ex: +33 6 11 22 33 06"
                    aria-invalid={!isPhoneValid}
                  />
                  {!isPhoneValid ? (
                    <FieldError>
                      Veuillez saisir un numéro français valide.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
            </div>

            <Button
              type="button"
              onClick={handleSearchClick}
              disabled={!canSearch || isSearchLoading}
              aria-disabled={!canSearch || isSearchLoading}
            >
              {isSearchLoading ? "Recherche…" : searchButtonLabel}
            </Button>
          </>
        ) : null}

        {showNoCriteria ? (
          <Alert>
            <AlertDescription>
              Saisissez au moins un critère pour lancer la recherche.
            </AlertDescription>
          </Alert>
        ) : null}

        {showNoInternalCollaborator &&
        outcome?.kind === "no_internal_collaborator" ? (
          <Alert variant="destructive">
            <AlertTitle>
              {noInternalCollaboratorAlert?.title ??
                "Collaborateur groupe introuvable"}
            </AlertTitle>
            <AlertDescription>
              {noInternalCollaboratorAlert?.description ?? outcome.message}
            </AlertDescription>
          </Alert>
        ) : null}

        {showServerError && outcome?.kind === "error" ? (
          <Alert variant="destructive">
            <AlertTitle>Erreur serveur</AlertTitle>
            <AlertDescription>{outcome.message}</AlertDescription>
          </Alert>
        ) : null}

        {showList ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">{resultsLabel(list.length)}</p>
            <p className="text-muted-foreground text-sm">
              Cliquez sur une carte pour la sélectionner.
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {list.map((individual) => {
                const isSelectedCard = selectedIndividualId === individual.id;
                const hasEditableSnapshot =
                  isSelectedCard &&
                  Boolean(ficheEdit) &&
                  selectedIndividual?.id === individual.id;
                const hasEditedValues = Boolean(
                  hasEditableSnapshot &&
                  ficheEdit &&
                  selectedIndividual &&
                  (isTrimmedFieldModified(
                    ficheEdit.email,
                    selectedIndividual.email,
                  ) ||
                    isTrimmedFieldModified(
                      ficheEdit.phone,
                      selectedIndividual.phone,
                    ) ||
                    isTrimmedFieldModified(
                      ficheEdit.addressLine,
                      selectedIndividual.addressLine,
                    ) ||
                    isTrimmedFieldModified(
                      ficheEdit.postalCode,
                      selectedIndividual.postalCode,
                    ) ||
                    isTrimmedFieldModified(
                      ficheEdit.city,
                      selectedIndividual.city,
                    )),
                );

                const addressPreviousLine =
                  hasEditableSnapshot && ficheEdit && selectedIndividual
                    ? editedFieldPreviousLine(
                        ficheEdit,
                        selectedIndividual,
                        "addressLine",
                      )
                    : null;
                const postalPreviousLine =
                  hasEditableSnapshot && ficheEdit && selectedIndividual
                    ? editedFieldPreviousLine(
                        ficheEdit,
                        selectedIndividual,
                        "postalCode",
                      )
                    : null;
                const cityPreviousLine =
                  hasEditableSnapshot && ficheEdit && selectedIndividual
                    ? editedFieldPreviousLine(
                        ficheEdit,
                        selectedIndividual,
                        "city",
                      )
                    : null;
                const phonePreviousLine =
                  hasEditableSnapshot && ficheEdit && selectedIndividual
                    ? editedFieldPreviousLine(
                        ficheEdit,
                        selectedIndividual,
                        "phone",
                      )
                    : null;
                const emailPreviousLine =
                  hasEditableSnapshot && ficheEdit && selectedIndividual
                    ? editedFieldPreviousLine(
                        ficheEdit,
                        selectedIndividual,
                        "email",
                      )
                    : null;

                const displayAddress =
                  hasEditableSnapshot && ficheEdit
                    ? ficheEdit.addressLine
                    : individual.addressLine;
                const displayPostalCode =
                  hasEditableSnapshot && ficheEdit
                    ? ficheEdit.postalCode
                    : individual.postalCode;
                const displayCity =
                  hasEditableSnapshot && ficheEdit
                    ? ficheEdit.city
                    : individual.city;
                const displayPhone =
                  hasEditableSnapshot && ficheEdit
                    ? ficheEdit.phone
                    : individual.phone;
                const displayEmail =
                  hasEditableSnapshot && ficheEdit
                    ? ficheEdit.email
                    : individual.email;

                return (
                  <Card
                    key={individual.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      isSelectedCard
                        ? "border-primary bg-primary/5 ring-primary/25 ring-2"
                        : "hover:bg-muted/50 hover:border-primary/40",
                    )}
                    onClick={() => handleSelectClient(individual.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectClient(individual.id);
                      }
                    }}
                  >
                    <div className="p-4 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="font-medium">
                            {individual.firstName} {individual.lastName}
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field>
                              <FieldLabel>Nom</FieldLabel>
                              <FieldContent>
                                <p>{individual.lastName}</p>
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Prénom</FieldLabel>
                              <FieldContent>
                                <p>{individual.firstName}</p>
                              </FieldContent>
                            </Field>
                            {additionalDetails?.(individual)}
                            <Field className="md:col-span-2">
                              <FieldLabel>Adresse</FieldLabel>
                              <FieldContent>
                                <p>{displayAddress}</p>
                                {addressPreviousLine ? (
                                  <p className="text-muted-foreground text-xs">
                                    Ancienne valeur : {addressPreviousLine}
                                  </p>
                                ) : null}
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Code postal</FieldLabel>
                              <FieldContent>
                                <p>{displayPostalCode}</p>
                                {postalPreviousLine ? (
                                  <p className="text-muted-foreground text-xs">
                                    Ancienne valeur : {postalPreviousLine}
                                  </p>
                                ) : null}
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Ville</FieldLabel>
                              <FieldContent>
                                <p>{displayCity}</p>
                                {cityPreviousLine ? (
                                  <p className="text-muted-foreground text-xs">
                                    Ancienne valeur : {cityPreviousLine}
                                  </p>
                                ) : null}
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Numéro de tel</FieldLabel>
                              <FieldContent>
                                <p>{displayPhone}</p>
                                {phonePreviousLine ? (
                                  <p className="text-muted-foreground text-xs">
                                    Ancienne valeur : {phonePreviousLine}
                                  </p>
                                ) : null}
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Email</FieldLabel>
                              <FieldContent>
                                <p>{displayEmail}</p>
                                {emailPreviousLine ? (
                                  <p className="text-muted-foreground text-xs">
                                    Ancienne valeur : {emailPreviousLine}
                                  </p>
                                ) : null}
                              </FieldContent>
                            </Field>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          {isSelectedCard ? (
                            <Badge className="gap-1">
                              <CheckCircle2 className="size-3.5" />
                              {selectedBadgeLabel}
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Cliquer pour sélectionner
                            </Badge>
                          )}
                          {hasEditedValues ? (
                            <Badge variant="secondary">{editBadgeLabel}</Badge>
                          ) : null}
                          <Button
                            type="button"
                            size="icon"
                            variant={
                              isSelectedCard && isEditEnabled
                                ? "default"
                                : "outline"
                            }
                            className={
                              isSelectedCard && isEditEnabled
                                ? "border-primary bg-primary text-primary-foreground"
                                : undefined
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClient(individual.id);
                            }}
                            aria-label={`${editAriaLabelPrefix} ${individual.firstName} ${individual.lastName}`}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : null}

        {selectedIndividual && ficheEdit && isEditEnabled ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-fe`}>Email</FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-fe`}
                  type="email"
                  value={ficheEdit.email}
                  onChange={(e) =>
                    onFicheChange({
                      ...ficheEdit,
                      email: e.target.value,
                    })
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-fp`}>Téléphone</FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-fp`}
                  value={ficheEdit.phone}
                  onChange={(e) =>
                    onFicheChange({
                      ...ficheEdit,
                      phone: e.target.value,
                    })
                  }
                />
              </FieldContent>
            </Field>
            <Field className="md:col-span-2">
              <FieldLabel htmlFor={`${idPrefix}-fa`}>
                Adresse (ligne)
              </FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-fa`}
                  value={ficheEdit.addressLine}
                  onChange={(e) =>
                    onFicheChange({
                      ...ficheEdit,
                      addressLine: e.target.value,
                    })
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-fpc`}>Code postal</FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-fpc`}
                  value={ficheEdit.postalCode}
                  onChange={(e) =>
                    onFicheChange({
                      ...ficheEdit,
                      postalCode: e.target.value,
                    })
                  }
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-fc`}>Ville</FieldLabel>
              <FieldContent>
                <Input
                  id={`${idPrefix}-fc`}
                  value={ficheEdit.city}
                  onChange={(e) =>
                    onFicheChange({
                      ...ficheEdit,
                      city: e.target.value,
                    })
                  }
                />
              </FieldContent>
            </Field>
          </div>
        ) : null}

        {showCreate && creationConfig ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {creationConfig.emptyResultsLabel}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleToggleCreateForm}
            >
              <UserPlus className="mr-2 size-4" />
              {showCreateForm
                ? creationConfig.closeCreateLabel
                : creationConfig.openCreateLabel}
            </Button>
            {canShowCreateForm
              ? creationConfig.renderCreateForm(
                  creationConfig.onCreateCanProceedChange,
                )
              : null}
          </div>
        ) : null}

        {showCreate && !creationConfig && noResultsAlert ? (
          <Alert variant="destructive">
            <AlertTitle>{noResultsAlert.title}</AlertTitle>
            <AlertDescription>{noResultsAlert.description}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

export { IndividualsSection };
export type { EditableIndividualProfile, NoResultsAlertConfig };
